if (!customElements.get('product-info')) {
  customElements.define(
    'product-info',
    class ProductInfo extends HTMLElement {
      quantityInput = undefined;
      quantityForm = undefined;
      onVariantChangeUnsubscriber = undefined;
      cartUpdateUnsubscriber = undefined;
      abortController = undefined;
      pendingRequestUrl = null;
      preProcessHtmlCallbacks = [];
      postProcessHtmlCallbacks = [];

      constructor() {
        super();

        this.quantityInput = this.querySelector('.quantity__input');
      }

      connectedCallback() {
        this.initializeProductSwapUtility();

        this.onVariantChangeUnsubscriber = subscribe(
          PUB_SUB_EVENTS.optionValueSelectionChange,
          this.handleOptionValueChange.bind(this)
        );

        this.initQuantityHandlers();
        this.initDuftplanetQuantityAvailability();
        this.dispatchEvent(new CustomEvent('product-info:loaded', { bubbles: true }));
      }

      addPreProcessCallback(callback) {
        this.preProcessHtmlCallbacks.push(callback);
      }

      initQuantityHandlers() {
        if (!this.quantityInput) return;

        this.quantityForm = this.querySelector('.product-form__quantity');
        if (!this.quantityForm) return;

        this.setQuantityBoundries();
        if (!this.dataset.originalSection) {
          this.cartUpdateUnsubscriber = subscribe(PUB_SUB_EVENTS.cartUpdate, this.fetchQuantityRules.bind(this));
        }
      }

      disconnectedCallback() {
        this.onVariantChangeUnsubscriber?.();
        this.removeEventListener('input', this.duftplanetQuantityHandler);
        this.removeEventListener('change', this.duftplanetQuantityHandler);
        this.cartUpdateUnsubscriber?.();
      }

      initializeProductSwapUtility() {
        this.preProcessHtmlCallbacks.push((html) =>
          html.querySelectorAll('.scroll-trigger').forEach((element) => element.classList.add('scroll-trigger--cancel'))
        );
        this.postProcessHtmlCallbacks.push((newNode) => {
          window?.Shopify?.PaymentButton?.init();
          window?.ProductModel?.loadShopifyXR();
        });
      }

      handleOptionValueChange({ data: { event, target, selectedOptionValues } }) {
        if (!this.contains(event.target)) return;

        this.duftplanetCartRequestId = (this.duftplanetCartRequestId || 0) + 1;
        this.setDuftplanetAvailabilityPending();
        this.resetProductFormState();

        const productUrl = target.dataset.productUrl || this.pendingRequestUrl || this.dataset.url;
        this.pendingRequestUrl = productUrl;
        const shouldSwapProduct = this.dataset.url !== productUrl;
        const shouldFetchFullPage = this.dataset.updateUrl === 'true' && shouldSwapProduct;

        this.renderProductInfo({
          requestUrl: this.buildRequestUrlWithParams(productUrl, selectedOptionValues, shouldFetchFullPage),
          targetId: target.id,
          callback: shouldSwapProduct
            ? this.handleSwapProduct(productUrl, shouldFetchFullPage)
            : this.handleUpdateProductInfo(productUrl),
        });
      }

      resetProductFormState() {
        const productForm = this.productForm;
        productForm?.toggleSubmitButton(true);
        productForm?.handleErrorMessage();
      }

      handleSwapProduct(productUrl, updateFullPage) {
        return (html) => {
          this.productModal?.remove();

          const selector = updateFullPage ? "product-info[id^='MainProduct']" : 'product-info';
          const sourceProductInfo = html.querySelector(selector);
          const variant = this.getSelectedVariant(sourceProductInfo);
          this.variantSelectors?.resolvePendingSelectPromise(variant, this.getVariantSelects(sourceProductInfo));
          this.updateURL(productUrl, variant?.id);

          if (updateFullPage) {
            document.querySelector('head title').innerHTML = html.querySelector('head title').innerHTML;

            HTMLUpdateUtility.viewTransition(
              document.querySelector('main'),
              html.querySelector('main'),
              this.preProcessHtmlCallbacks,
              this.postProcessHtmlCallbacks
            );
          } else {
            HTMLUpdateUtility.viewTransition(
              this,
              html.querySelector('product-info'),
              this.preProcessHtmlCallbacks,
              this.postProcessHtmlCallbacks
            );
          }
        };
      }

      renderProductInfo({ requestUrl, targetId, callback }) {
        this.abortController?.abort();
        this.abortController = new AbortController();

        fetch(requestUrl, { signal: this.abortController.signal })
          .then((response) => response.text())
          .then((responseText) => {
            this.pendingRequestUrl = null;
            const html = new DOMParser().parseFromString(responseText, 'text/html');
            callback(html);
            // set focus to last clicked option value
            document.querySelector(`#${targetId}`)?.focus();
          })
          .catch((error) => {
            if (error.name === 'AbortError') {
              console.log('Fetch aborted by user');
            } else {
              console.error(error);
              this.setDuftplanetAvailabilityError();
            }
            this.variantSelectors?.rejectPendingSelectPromise(error);
          });
      }

      parseJsonScript(parent, selector) {
        try {
          return JSON.parse(parent?.querySelector(selector)?.textContent);
        } catch {
          return null;
        }
      }

      getVariantSelects(queryRoot) {
        return queryRoot?.querySelector('variant-selects');
      }

      getSelectedVariant(productInfoNode) {
        return this.parseJsonScript(this.getVariantSelects(productInfoNode), '[data-selected-variant]');
      }

      buildRequestUrlWithParams(url, optionValues, shouldFetchFullPage = false) {
        const params = [];

        !shouldFetchFullPage && params.push(`section_id=${this.sectionId}`);

        if (optionValues.length) {
          params.push(`option_values=${optionValues.join(',')}`);
        }

        return `${url}?${params.join('&')}`;
      }

      updateOptionValues(html) {
        const variantSelects = html.querySelector('variant-selects');
        if (variantSelects) {
          HTMLUpdateUtility.viewTransition(this.variantSelectors, variantSelects, this.preProcessHtmlCallbacks);
        }
      }

      handleUpdateProductInfo(productUrl) {
        return (html) => {
          const sourceVariantSelects = this.getVariantSelects(html);
          const variant = this.getSelectedVariant(html);

          // Resolve product:select promise before updateOptionValues replaces the variant-selects DOM element
          this.variantSelectors?.resolvePendingSelectPromise(variant, sourceVariantSelects);

          this.pickupAvailability?.update(variant);
          this.updateOptionValues(html);
          this.updateURL(productUrl, variant?.id);
          this.updateVariantInputs(variant?.id);

          if (!variant) {
            this.setUnavailable();
            return;
          }

          this.updateMedia(html, variant?.featured_media?.id);

          const updateSourceFromDestination = (id, shouldHide = (source) => false) => {
            const source = html.getElementById(`${id}-${this.sectionId}`);
            const destination = this.querySelector(`#${id}-${this.dataset.section}`);
            if (source && destination) {
              destination.innerHTML = source.innerHTML;
              destination.classList.toggle('hidden', shouldHide(source));
            }
          };

          updateSourceFromDestination('price');
          updateSourceFromDestination('Sku', ({ classList }) => classList.contains('hidden'));
          updateSourceFromDestination('Inventory', ({ innerText }) => innerText === '');
          this.updateDuftplanetQuantityAvailability(html);
          updateSourceFromDestination('Volume');
          updateSourceFromDestination('Price-Per-Item', ({ classList }) => classList.contains('hidden'));

          this.updateQuantityRules(this.sectionId, html);
          this.renderDuftplanetQuantityAvailability();
          this.querySelector(`#Quantity-Rules-${this.dataset.section}`)?.classList.remove('hidden');
          this.querySelector(`#Volume-Note-${this.dataset.section}`)?.classList.remove('hidden');

          this.productForm?.toggleSubmitButton(
            html.getElementById(`ProductSubmitButton-${this.sectionId}`)?.hasAttribute('disabled') ?? true,
            window.variantStrings.soldOut
          );

          publish(PUB_SUB_EVENTS.variantChange, {
            data: {
              sectionId: this.sectionId,
              html,
              variant,
            },
          });
        };
      }

      updateVariantInputs(variantId) {
        this.querySelectorAll(
          `#product-form-${this.dataset.section}, #product-form-installment-${this.dataset.section}`
        ).forEach((productForm) => {
          const input = productForm.querySelector('input[name="id"]');
          input.value = variantId ?? '';
          input.dispatchEvent(new Event('change', { bubbles: true }));
        });
      }

      updateURL(url, variantId) {
        this.querySelector('share-button')?.updateUrl(
          `${window.shopUrl}${url}${variantId ? `?variant=${variantId}` : ''}`
        );

        if (this.dataset.updateUrl === 'false') return;
        window.history.replaceState({}, '', `${url}${variantId ? `?variant=${variantId}` : ''}`);
      }

      setUnavailable() {
        const notice = this.querySelector('[data-duftplanet-quantity-notice]');
        if (notice) notice.hidden = true;
        this.productForm?.toggleSubmitButton(true, window.variantStrings.unavailable);

        const selectors = ['price', 'Inventory', 'Sku', 'Price-Per-Item', 'Volume-Note', 'Volume', 'Quantity-Rules']
          .map((id) => `#${id}-${this.dataset.section}`)
          .join(', ');
        document.querySelectorAll(selectors).forEach(({ classList }) => classList.add('hidden'));
      }

      updateMedia(html, variantFeaturedMediaId) {
        if (!variantFeaturedMediaId) return;

        const mediaGallerySource = this.querySelector('media-gallery ul');
        const mediaGalleryDestination = html.querySelector(`media-gallery ul`);

        const refreshSourceData = () => {
          if (this.hasAttribute('data-zoom-on-hover')) enableZoomOnHover(2);
          const mediaGallerySourceItems = Array.from(mediaGallerySource.querySelectorAll('li[data-media-id]'));
          const sourceSet = new Set(mediaGallerySourceItems.map((item) => item.dataset.mediaId));
          const sourceMap = new Map(
            mediaGallerySourceItems.map((item, index) => [item.dataset.mediaId, { item, index }])
          );
          return [mediaGallerySourceItems, sourceSet, sourceMap];
        };

        if (mediaGallerySource && mediaGalleryDestination) {
          let [mediaGallerySourceItems, sourceSet, sourceMap] = refreshSourceData();
          const mediaGalleryDestinationItems = Array.from(
            mediaGalleryDestination.querySelectorAll('li[data-media-id]')
          );
          const destinationSet = new Set(mediaGalleryDestinationItems.map(({ dataset }) => dataset.mediaId));
          let shouldRefresh = false;

          // add items from new data not present in DOM
          for (let i = mediaGalleryDestinationItems.length - 1; i >= 0; i--) {
            if (!sourceSet.has(mediaGalleryDestinationItems[i].dataset.mediaId)) {
              mediaGallerySource.prepend(mediaGalleryDestinationItems[i]);
              shouldRefresh = true;
            }
          }

          // remove items from DOM not present in new data
          for (let i = 0; i < mediaGallerySourceItems.length; i++) {
            if (!destinationSet.has(mediaGallerySourceItems[i].dataset.mediaId)) {
              mediaGallerySourceItems[i].remove();
              shouldRefresh = true;
            }
          }

          // refresh
          if (shouldRefresh) [mediaGallerySourceItems, sourceSet, sourceMap] = refreshSourceData();

          // if media galleries don't match, sort to match new data order
          mediaGalleryDestinationItems.forEach((destinationItem, destinationIndex) => {
            const sourceData = sourceMap.get(destinationItem.dataset.mediaId);

            if (sourceData && sourceData.index !== destinationIndex) {
              mediaGallerySource.insertBefore(
                sourceData.item,
                mediaGallerySource.querySelector(`li:nth-of-type(${destinationIndex + 1})`)
              );

              // refresh source now that it has been modified
              [mediaGallerySourceItems, sourceSet, sourceMap] = refreshSourceData();
            }
          });
        }

        // set featured media as active in the media gallery
        this.querySelector(`media-gallery`)?.setActiveMedia?.(
          `${this.dataset.section}-${variantFeaturedMediaId}`,
          true
        );

        // update media modal
        const modalContent = this.productModal?.querySelector(`.product-media-modal__content`);
        const newModalContent = html.querySelector(`product-modal .product-media-modal__content`);
        if (modalContent && newModalContent) modalContent.innerHTML = newModalContent.innerHTML;
      }

      setQuantityBoundries() {
        const data = {
          cartQuantity: this.quantityInput.dataset.cartQuantity ? parseInt(this.quantityInput.dataset.cartQuantity) : 0,
          min: this.quantityInput.dataset.min ? parseInt(this.quantityInput.dataset.min) : 1,
          max: this.quantityInput.dataset.max ? parseInt(this.quantityInput.dataset.max) : null,
          step: this.quantityInput.step ? parseInt(this.quantityInput.step) : 1,
        };

        let min = data.min;
        const max = data.max === null ? data.max : data.max - data.cartQuantity;
        if (max !== null) min = Math.min(min, max);
        if (data.cartQuantity >= data.min) min = Math.min(min, data.step);

        this.quantityInput.min = min;

        if (max) {
          this.quantityInput.max = max;
        } else {
          this.quantityInput.removeAttribute('max');
        }
        this.quantityInput.value = min;

        publish(PUB_SUB_EVENTS.quantityUpdate, undefined);
        this.renderDuftplanetQuantityAvailability();
      }

      fetchQuantityRules() {
        const currentVariantId = this.productForm?.variantIdInput?.value;
        if (!currentVariantId) return;
        const requestId = (this.duftplanetCartRequestId || 0) + 1;
        this.duftplanetCartRequestId = requestId;
        const spinner = this.querySelector('.quantity__rules-cart .loading__spinner');
        spinner?.classList.remove('hidden');
        this.setDuftplanetAvailabilityPending();
        return fetch(`${this.dataset.url}?variant=${currentVariantId}&section_id=${this.dataset.section}`, { cache: 'no-store' })
          .then((response) => {
            if (!response.ok) throw new Error('Availability refresh failed');
            return response.text();
          })
          .then((responseText) => {
            if (!this.isConnected || requestId !== this.duftplanetCartRequestId ||
                String(this.productForm?.variantIdInput?.value) !== String(currentVariantId)) return;
            const html = new DOMParser().parseFromString(responseText, 'text/html');
            this.updateQuantityRules(this.dataset.section, html);
            this.updateDuftplanetQuantityAvailability(html, this.dataset.section);
            this.renderDuftplanetQuantityAvailability();
          })
          .catch((error) => {
            if (requestId !== this.duftplanetCartRequestId ||
                String(this.productForm?.variantIdInput?.value) !== String(currentVariantId)) return;
            console.error(error);
            this.setDuftplanetAvailabilityError();
          })
          .finally(() => {
            if (requestId === this.duftplanetCartRequestId) spinner?.classList.add('hidden');
          });
      }

      updateQuantityRules(sectionId, html) {
        if (!this.quantityInput) return;
        this.setQuantityBoundries();

        const quantityFormUpdated = html.getElementById(`Quantity-Form-${sectionId}`);
        const selectors = ['.quantity__input', '.quantity__rules', '.quantity__label'];
        for (let selector of selectors) {
          const current = this.quantityForm.querySelector(selector);
          const updated = quantityFormUpdated.querySelector(selector);
          if (!current || !updated) continue;
          if (selector === '.quantity__input') {
            const attributes = ['data-cart-quantity', 'data-min', 'data-max', 'step'];
            for (let attribute of attributes) {
              const valueUpdated = updated.getAttribute(attribute);
              if (valueUpdated !== null) {
                current.setAttribute(attribute, valueUpdated);
              } else {
                current.removeAttribute(attribute);
              }
            }
          } else {
            current.innerHTML = updated.innerHTML;
            if (selector === '.quantity__label') {
              const updatedAriaLabelledBy = updated.getAttribute('aria-labelledby');
              if (updatedAriaLabelledBy) {
                current.setAttribute('aria-labelledby', updatedAriaLabelledBy);
                // Update the referenced visually hidden element
                const labelId = updatedAriaLabelledBy;
                const currentHiddenLabel = document.getElementById(labelId);
                const updatedHiddenLabel = html.getElementById(labelId);
                if (currentHiddenLabel && updatedHiddenLabel) {
                  currentHiddenLabel.textContent = updatedHiddenLabel.textContent;
                }
              }
            }
          }
        }
      }

      initDuftplanetQuantityAvailability() {
        if (!this.querySelector('[data-duftplanet-availability]')) return;
        this.duftplanetQuantityHandler = (event) => {
          if (event.target.matches('.quantity__input')) this.renderDuftplanetQuantityAvailability();
        };
        this.addEventListener('input', this.duftplanetQuantityHandler);
        this.addEventListener('change', this.duftplanetQuantityHandler);
        this.renderDuftplanetQuantityAvailability();
      }

      updateDuftplanetQuantityAvailability(html, sectionId = this.sectionId) {
        const source = html.getElementById(`QuantityAvailability-${sectionId}`);
        const destination = this.querySelector(`#QuantityAvailability-${this.dataset.section}`);
        if (source && destination) destination.innerHTML = source.innerHTML;
        this.duftplanetAvailabilityStale = false;
      }

      setDuftplanetDeliveryStatus(color, title, detail = '') {
        const inventory = this.querySelector(`#Inventory-${this.dataset.section}`);
        if (!inventory) return;
        inventory.classList.remove('hidden');
        const text = detail ? `${title} - ${detail}` : title;
        const previous = inventory.querySelector('.duftplanet-delivery-status__row');
        if (previous?.textContent === text && previous.dataset.color === color) return;
        const row = document.createElement('div');
        row.className = 'duftplanet-delivery-status__row';
        row.dataset.color = color;
        const dot = document.createElement('span');
        dot.className = `duftplanet-delivery-dot duftplanet-delivery-dot--${color}`;
        dot.setAttribute('aria-hidden', 'true');
        const label = document.createElement('span');
        const strong = document.createElement('strong');
        strong.textContent = title;
        label.append(strong);
        if (detail) label.append(document.createTextNode(` - ${detail}`));
        row.append(dot, label);
        inventory.replaceChildren(row);
      }

      setDuftplanetAvailabilityPending() {
        if (!this.querySelector('[data-duftplanet-availability]')) return;
        this.duftplanetAvailabilityStale = true;
        const panel = this.querySelector('[data-duftplanet-quantity-notice]');
        if (panel) panel.hidden = true;
        this.setDuftplanetDeliveryStatus('blue', 'Verfügbarkeit wird aktualisiert …');
      }

      setDuftplanetAvailabilityError() {
        if (!this.querySelector('[data-duftplanet-availability]')) return;
        this.duftplanetAvailabilityStale = true;
        this.setDuftplanetDeliveryStatus('blue', 'Lieferzeit bitte erneut prüfen');
      }

      renderDuftplanetQuantityAvailability() {
        if (this.duftplanetAvailabilityStale) return;
        const root = this.querySelector('[data-duftplanet-availability]');
        const script = root?.querySelector('[data-duftplanet-availability-data]');
        const panel = root?.querySelector('[data-duftplanet-quantity-notice]');
        if (!script || !panel || !this.quantityInput) return;
        let data;
        try {
          data = JSON.parse(script.textContent);
        } catch {
          panel.hidden = true;
          return;
        }
        const variantId = this.productForm?.variantIdInput?.value || data.variantId;
        if (String(variantId) !== String(data.variantId)) {
          panel.hidden = true;
          return;
        }
        const requested = Number(this.quantityInput.value);
        if (!Number.isInteger(requested) || requested < 1) {
          panel.hidden = true;
          this.setDuftplanetDeliveryStatus('blue', 'Bitte eine gültige Menge wählen');
          return;
        }
        const cartQuantity = Math.max(0, Number(data.cartQuantity) || 0);
        const stock = Math.max(0, Number(data.inventoryQuantity) || 0);
        const total = requested + cartQuantity;
        const fastDelivery = data.fastDelivery || '2-3 Werktage';
        const orderDelivery = data.orderDelivery || '7-10 Werktage';
        const stockLine = panel.querySelector('[data-duftplanet-stock-line]');
        const stockText = panel.querySelector('[data-duftplanet-stock-text]');
        const orderLine = panel.querySelector('[data-duftplanet-order-line]');
        const orderText = panel.querySelector('[data-duftplanet-order-text]');
        const cartText = panel.querySelector('[data-duftplanet-cart-text]');
        const summary = panel.querySelector('[data-duftplanet-summary]');
        panel.hidden = true;

        if (!data.available) {
          this.setDuftplanetDeliveryStatus('red', 'Derzeit nicht verfügbar');
          return;
        }
        // Untracked inventory is not evidence of physical stock.
        if (!data.tracked) {
          this.setDuftplanetDeliveryStatus('blue', 'Verfügbar', `Lieferzeit ${orderDelivery}`);
          return;
        }
        if (total <= stock) {
          this.setDuftplanetDeliveryStatus('green', 'Auf Lager', `Lieferzeit ${fastDelivery}`);
          return;
        }
        const remaining = Math.max(0, stock - cartQuantity);
        const stocked = Math.min(requested, remaining);
        const toOrder = requested - stocked;
        const continueSelling = data.inventoryPolicy === 'continue';

        if (!continueSelling) {
          this.setDuftplanetDeliveryStatus('red', 'Gewünschte Menge nicht verfügbar');
          stockLine.hidden = true;
          orderLine.hidden = false;
          orderText.textContent = remaining > 0
            ? `Noch ${remaining} ${remaining === 1 ? 'Stück ist' : 'Stück sind'} zusätzlich verfügbar.`
            : 'Keine weiteren Stücke dieser Variante verfügbar.';
          summary.textContent = 'Bitte passe die Menge an.';
        } else {
          this.setDuftplanetDeliveryStatus('blue', 'Verfügbar', `Lieferzeit ${orderDelivery}`);
          // A completely unstocked variant already has a clear delivery line above.
          if (stock === 0 && cartQuantity === 0) return;
          this.setDuftplanetDeliveryStatus('blue', 'Verfügbar', 'Lieferzeit siehe unten');
          stockLine.hidden = stocked === 0;
          stockText.textContent = `${stocked} auf Lager`;
          orderLine.hidden = false;
          orderText.textContent = stocked > 0
          ? `${toOrder} ${toOrder === 1 ? 'weiteres' : 'weitere'} für dich bestellbar`
          : `${toOrder} für dich bestellbar`;
          summary.textContent = `Gemeinsame Lieferung in ${orderDelivery}.`;
        }
        cartText.hidden = cartQuantity === 0;
        cartText.textContent = cartQuantity > 0
          ? `Bereits ${cartQuantity} Stück dieser Variante im Warenkorb berücksichtigt.`
          : '';
        panel.hidden = false;
      }

      get productForm() {
        return this.querySelector(`product-form`);
      }

      get productModal() {
        return document.querySelector(`#ProductModal-${this.dataset.section}`);
      }

      get pickupAvailability() {
        return this.querySelector(`pickup-availability`);
      }

      get variantSelectors() {
        return this.querySelector('variant-selects');
      }

      get relatedProducts() {
        const relatedProductsSectionId = SectionId.getIdForSection(
          SectionId.parseId(this.sectionId),
          'related-products'
        );
        return document.querySelector(`product-recommendations[data-section-id^="${relatedProductsSectionId}"]`);
      }

      get quickOrderList() {
        const quickOrderListSectionId = SectionId.getIdForSection(
          SectionId.parseId(this.sectionId),
          'quick_order_list'
        );
        return document.querySelector(`quick-order-list[data-id^="${quickOrderListSectionId}"]`);
      }

      get sectionId() {
        return this.dataset.originalSection || this.dataset.section;
      }
    }
  );
}
